import { useEffect, useRef } from 'react'
import { useTranslation } from '@kostbar/i18n'
import type { ReportDimension } from '@/features/reports/api/use-sales-report-query'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

/**
 * The six things a manager breaks revenue down by, and the second-level switch each one carries
 * (CHG-2026-09-manager-reporting).
 *
 * The two levels compose into one flat wire value, so the server keeps one enum and one mapping and
 * a new breakdown never becomes a new page.
 */
export type DimensionGroup =
  | 'time'
  | 'channel'
  | 'category'
  | 'article'
  | 'payment'
  | 'location'

const GROUP_LEVELS: Record<DimensionGroup, Array<ReportDimension>> = {
  time: ['time_week', 'time_day', 'time_hour'],
  channel: ['channel'],
  category: ['category_main', 'category_sub'],
  article: ['article'],
  payment: ['payment_method'],
  location: ['restaurant'],
}

const GROUP_ORDER: Array<DimensionGroup> = [
  'time',
  'channel',
  'category',
  'article',
  'payment',
  'location',
]

export function groupOf(dimension: ReportDimension): DimensionGroup {
  const found = GROUP_ORDER.find((group) =>
    GROUP_LEVELS[group].includes(dimension),
  )
  // weekday_hour and daypart are dashboard-only shapes of the time question; they belong to the
  // time group even though the switcher does not offer them as levels.
  return found ?? 'time'
}

export function levelsOf(group: DimensionGroup): Array<ReportDimension> {
  return GROUP_LEVELS[group]
}

interface DimensionSwitcherProps {
  value: ReportDimension
  onChange: (dimension: ReportDimension) => void
}

export function DimensionSwitcher({ value, onChange }: DimensionSwitcherProps) {
  const { t } = useTranslation('reports')
  const { can } = usePermissions()
  const group = groupOf(value)
  const levels = levelsOf(group)

  // The six groups do not fit a phone, so the track scrolls — and a link landing on "Standort"
  // used to open with the active chip off the right edge and the row reading as if nothing were
  // selected at all. `nearest` on both axes so this never scrolls the page itself.
  const activeChip = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    activeChip.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [group])

  // The article breakdown is the article report's own permission, so a role without it is not
  // offered the group at all — better than a chip that answers every click with a 403.
  const groups = can(ACTIONS.REPORT_ARTICLES_VIEW)
    ? GROUP_ORDER
    : GROUP_ORDER.filter((g) => g !== 'article')

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {/* The six groups do not fit a 375px row. They scroll inside their own track rather than
          being clipped by an ancestor, which is what hid "Zahlart" off the right edge entirely. */}
      <div className="flex min-w-0 max-w-full items-center gap-2 overflow-x-auto">
        <span className="hidden shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:inline">
          {t('dimension.label')}
        </span>
        <ToggleGroup
          type="single"
          variant="outline"
          value={group}
          onValueChange={(next) => {
            if (!next) return
            // Switching group lands on that group's first level, which is the one a reader means.
            onChange(GROUP_LEVELS[next as DimensionGroup][0])
          }}
          aria-label={t('dimension.label')}
          className="shrink-0"
        >
          {groups.map((option) => (
            <ToggleGroupItem
              key={option}
              value={option}
              ref={option === group ? activeChip : undefined}
              className="h-12 shrink-0 sm:h-9"
            >
              {t(`dimension.groups.${option}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {levels.length > 1 ? (
        <ToggleGroup
          type="single"
          variant="outline"
          value={value}
          onValueChange={(next) => {
            if (next) onChange(next as ReportDimension)
          }}
          aria-label={t('dimension.levelLabel')}
        >
          {levels.map((level) => (
            <ToggleGroupItem
              key={level}
              value={level}
              className="h-12 shrink-0 sm:h-9"
            >
              {t(`dimension.levels.${level}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : null}
    </div>
  )
}
